import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Gantt } from '@bryntum/gantt';
import { WidgetsMapComponentKeys } from 'config/widgetConfigurations/widgetsConfigurations';
import { getStringWithoutHash } from 'utilitys/helpers/general';
import { WidgetView } from 'utilitys/reporting/widgetViewModeUtils';

import useCustomPageSubTitle from 'hooks/useCustomPageSubTitle';
import { useGlobalFiltersHook } from 'hooks/useGlobalFiltersHook';

import { useQueryActivitiesSummary } from 'api/queries/activitiesSummary.query';
import { useQueryGanttChart } from 'api/queries/ganttChart.query';

import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import {
    FilterNames,
    IGlobalFilterData,
    getFiltersData,
} from 'components/common/GlobalFilterComponent/GlobalFilterComponent.utils';
import Slider, { marks } from 'components/common/Slider/Slider';
import { TooltipIcon } from 'components/common/TooltipIcon/tooltipIcon';
import { WidgetExistInBackend } from 'components/common/WidgetExistInBackend/WidgetExistInBackend';
import { WidgetTitle } from 'components/common/WidgetTitle/WidgetTitle';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';

import SliderWrapper from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/ProgramProgressOverview/SliderWrapper';
import { AddWidget } from 'components/Dashboards/Project/Components/CustomDashboard/AddRemoveWidget/addWidget';
import { PinboardWidgetPills } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetPills/PinboardWidgetPills';
import { IComponentProps, IFilter } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { WidgetButton } from 'components/Dashboards/Reporting/_common/widgetButtons/WidgetButton';
import GanttChartDisplay from 'components/widgets/Gantt/components/GanttChartDisplay';
import { ProgressType } from 'components/widgets/Gantt/components/GanttChartDisplay.utils';
import { GanttSettingsModal } from 'components/widgets/Gantt/components/ganttSettingsModal';

interface IParams {
    filter: {
        filters: IFilter[];
    };
    id: string;
    maxLevel: number;
    minLevel: number;
}

export interface IGanttChartProps extends IComponentProps {
    maxLevel: number;
    globalFilterData: IGlobalFilterData | undefined;
}
export const GanttChart = ({
    latestVersionId,
    projectId,
    contractId,
    maxLevel: initialMaxLevel,
    widgetId,
    localFilters,
    editNarrative,
    externalComponents,
    setSubTitleForExport,
    globalFilterData,
    filter,
    widgetViewMode = WidgetView.page,
}: IGanttChartProps) => {
    const [progress, setProgress] = useState<ProgressType>(localFilters ? localFilters.progress : 'criticality');
    const [isCriticalPath, setIsCriticalPath] = useState<boolean>(localFilters ? localFilters?.isCriticalPath : false);
    const [isWbs, setIsWbs] = useState<boolean>(localFilters ? localFilters?.isWbs : false);
    const [isMilestone, setIsMilestone] = useState<boolean>(localFilters ? localFilters?.isMilestone : false);
    const [maxLevel, setMaxLevel] = useState<number>(localFilters ? localFilters?.maxLevel : 5);
    const [params, setParams] = useState<IParams | null>(null);
    const { data: summaryData } = useQueryActivitiesSummary(latestVersionId);
    const { data: ganttData, isFetching } = useQueryGanttChart(params);
    const [isFullScreen, setIsFullScreen] = useState<boolean>(false);
    const [showSettings, setShowSettings] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    const ganttInstance = useRef<Gantt | null>(null);

    const getInstance = useCallback((gantt) => {
        if (gantt) {
            ganttInstance.current = gantt;
        }
    }, []);

    const filters = useMemo(
        () =>
            widgetId
                ? filter?.filters || []
                : getFiltersData({
                      [FilterNames.ACTIVITY_CODES]: globalFilterData?.activityCodes
                          ?.filter((item) => !item.value.isExclude)
                          .map((i) => i.value.name),
                      [FilterNames.ACTIVITY_CODES_EXCLUDE]: globalFilterData?.activityCodes
                          ?.filter((item) => item.value.isExclude)
                          .map((i) => i.value.name),
                      [FilterNames.FRAGNET_SEARCH]: globalFilterData?.milestoneFragnet,
                      [FilterNames.TAG_CARD_BOARD]: globalFilterData?.tags
                          ?.filter((item) => !item.value.isExclude)
                          .map((i) => i.value.name),
                      [FilterNames.TAG_CARD_BOARD_EXCLUDE]: globalFilterData?.tags
                          ?.filter((item) => item.value.isExclude)
                          .map((i) => i.value.name),
                      [FilterNames.WBS_SEARCH]: globalFilterData?.wbs,
                  }),

        [filter, globalFilterData],
    );

    useEffect(() => {
        if (latestVersionId && maxLevel) {
            setParams({
                filter: {
                    filters: filters,
                },
                id: String(latestVersionId),
                maxLevel,
                minLevel: 0,
            });
        }
    }, [maxLevel, latestVersionId, filters]);

    useEffect(() => {
        const handleFullScreen = () => {
            if (document.fullscreenElement) {
                setIsFullScreen(true);
            } else {
                setIsFullScreen(false);
            }
        };

        document.addEventListener('fullscreenchange', handleFullScreen);
        return () => {
            document.removeEventListener('fullscreenchange', handleFullScreen);
        };
    }, []);

    const handleFullscreen = () => {
        if (ref.current) {
            if (!document.fullscreenElement) {
                ref.current.requestFullscreen();
            } else if (document.exitFullscreen) {
                document.exitFullscreen();
            }
        }
    };

    const zoomToFit = () => {
        const instance = ganttInstance.current;
        if (instance) {
            instance.zoomToFit();
        }
    };

    const { generateFilters: formattedFilter } = useGlobalFiltersHook({
        widgetId,
        filter: {
            filters: [
                { name: 'Progress', data: [localFilters?.progress] },
                { name: 'Max Level', data: [localFilters?.maxLevel] },
                { name: 'Critical Path Only', data: [localFilters?.isCriticalPath ? 'True' : 'False'] },
                { name: 'WBS Only', data: [localFilters?.isWbs ? 'True' : 'False'] },
                ...filters,
            ],
        },
        latestVersionId,
    });

    const title = 'Gantt Chart';
    const componentKey: WidgetsMapComponentKeys = 'ganttChart';

    const subTitle = useCustomPageSubTitle({ projectId, contractId, latestVersionId });

    const route = `${getStringWithoutHash(window.location.href)}`;

    useEffect(() => {
        setSubTitleForExport && setSubTitleForExport({ widgetId, subTitle, title });
    }, [widgetId, subTitle]);

    return (
        <WidgetExistInBackend componentKey={componentKey}>
            <WidgetWithTitle
                id={'Program Chart'}
                title={<WidgetTitle title={title} widgetViewMode={widgetViewMode} />}
                titleComponents={[
                    externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                    !widgetId && (
                        <SliderWrapper key={'Slider'}>
                            <Slider
                                defaultValue={5}
                                step={1}
                                min={1}
                                max={initialMaxLevel}
                                marks={marks(0, initialMaxLevel)}
                                onChangeCommitted={(e, value) => setMaxLevel(value)}
                            />
                        </SliderWrapper>
                    ),
                    <WidgetButton icon={'zoom_in_map'} onClick={zoomToFit} tooltipContent={`Zoom To Fit`} />,
                    <WidgetButton icon={'fullscreen'} onClick={handleFullscreen} tooltipContent={`Full Screen`} />,
                    !widgetId && (
                        <WidgetButton
                            icon={'tune'}
                            onClick={() => setShowSettings(true)}
                            tooltipContent={`Gantt Settings`}
                        />
                    ),
                    <AddWidget
                        key={'addWidget'}
                        title={title}
                        componentKey={componentKey}
                        projectId={projectId}
                        contractId={contractId}
                        widgetId={widgetId}
                        filters={filters}
                        route={route}
                        localFilters={{ progress, isCriticalPath, isWbs, maxLevel }}
                        shouldHide={true}
                    />,
                    <ExportDropdown title={title} key={'export'} subTitle={subTitle} />,
                    <TooltipIcon key={'info'} tooltip={ganttData?.info} />,
                ]}
                titleFilters={[
                    editNarrative && <div key={'editNarrative'}>{editNarrative}</div>,
                    widgetId && formattedFilter.length > 0 ? (
                        <PinboardWidgetPills key={'widgetFilters'} filters={formattedFilter} />
                    ) : null,
                ]}
            >
                <div ref={ref}>
                    <GanttChartDisplay
                        isFullScreen={isFullScreen}
                        ganttData={ganttData}
                        isLoading={isFetching}
                        summaryData={summaryData}
                        getInstanceAgain={getInstance}
                        widgetId={widgetId}
                        progress={progress}
                        setProgress={setProgress}
                        isCriticalPath={isCriticalPath}
                        setIsCriticalPath={setIsCriticalPath}
                        isWbs={isWbs}
                        setIsWbs={setIsWbs}
                        isMilestone={isMilestone}
                        setIsMilestone={setIsMilestone}
                        milestoneFragnet={globalFilterData?.milestoneFragnet}
                    />
                </div>
            </WidgetWithTitle>
            <GanttSettingsModal showModal={showSettings} onHideModal={() => setShowSettings(false)} />
        </WidgetExistInBackend>
    );
};
